import { PROTOCOL_CONSTANTS } from "@klass-share/protocol";
import * as Y from "yjs";
import { compareWhiteboardItems, nextFractionalIndex, prepareStrokePoints, } from "./geometry.js";
export const WHITEBOARD_PAGES_KEY = "pages";
export const DEFAULT_WHITEBOARD_PAGE_ID = "default";
const unsafeTextCharacters = new RegExp(String.raw `[\u0000-\u001f\u007f-\u009f\u200b-\u200d\u202a-\u202e\u2066-\u2069\ufeff]`, "gu");
export class WhiteboardDocument {
    doc;
    pages;
    localOrigin;
    undoManager;
    constructor(doc, localOrigin) {
        this.doc = doc;
        this.pages = doc.getMap(WHITEBOARD_PAGES_KEY);
        this.localOrigin = localOrigin;
        this.undoManager = new Y.UndoManager(this.pages, {
            trackedOrigins: new Set([localOrigin]),
            captureTimeout: PROTOCOL_CONSTANTS.WB_UNDO_CAPTURE_TIMEOUT,
        });
        for (const pageId of this.pages.keys())
            this.#trackPage(pageId);
        this.pages.observe((event) => {
            for (const [pageId, change] of event.changes.keys) {
                if (change.action === "add")
                    this.#trackPage(pageId);
            }
        });
    }
    /** Host/session initialization helper. Web participants never call this. */
    initializeDefaultPage(createdAt) {
        if (this.pages.has(DEFAULT_WHITEBOARD_PAGE_ID))
            return DEFAULT_WHITEBOARD_PAGE_ID;
        this.#createPage(DEFAULT_WHITEBOARD_PAGE_ID, {
            name: "1",
            createdAt,
            background: "plain",
        });
        return DEFAULT_WHITEBOARD_PAGE_ID;
    }
    /** Host-only numbered page creation seam; authorization lives in the host UI/runtime. */
    createNumberedPage(createdAt, pageId, background = "plain") {
        requireUuid(pageId, "pageId");
        if (this.pages.has(pageId))
            throw new Error("whiteboard page already exists");
        const pageNumber = this.listPages().length + 1;
        this.#createPage(pageId, {
            name: String(pageNumber),
            createdAt,
            background,
        });
        return pageId;
    }
    listPages() {
        const pages = [];
        for (const pageId of this.pages.keys()) {
            const collections = this.#page(pageId);
            if (collections !== undefined)
                pages.push({ pageId, meta: collections.meta });
        }
        return pages.sort((left, right) => pageNumber(left.meta.name) - pageNumber(right.meta.name) ||
            left.meta.name.localeCompare(right.meta.name) ||
            left.pageId.localeCompare(right.pageId));
    }
    pageSnapshot(pageId) {
        const collections = this.#page(pageId);
        if (collections === undefined)
            return undefined;
        const strokes = [...collections.strokes.entries()]
            .filter((entry) => isStrokePayload(entry[1]))
            .sort(compareWhiteboardItems);
        const objects = [...collections.objects.entries()]
            .filter((entry) => isObjectPayload(entry[1]))
            .sort(compareWhiteboardItems);
        return {
            pageId,
            meta: collections.meta,
            strokes,
            objects,
        };
    }
    commitStroke(input) {
        requireUuid(input.strokeId, "strokeId");
        const collections = this.#requirePage(input.pageId);
        if (collections.strokes.has(input.strokeId))
            throw new Error("whiteboard stroke ids are write-once");
        const points = prepareStrokePoints(input.pts);
        if (points.length < 4)
            throw new Error("a stroke needs at least two points");
        const payload = freezeStroke({
            v: 1,
            tool: input.tool,
            color: requireColor(input.color),
            width: requirePositive(input.width, "stroke width"),
            pts: points,
            z: requireZ(input.z ??
                nextFractionalIndex([
                    ...collections.strokes.values(),
                    ...collections.objects.values(),
                ].map((value) => value.z))),
            createdBy: this.localOrigin.clientId,
            createdAt: requireFinite(input.createdAt, "createdAt"),
        });
        this.doc.transact(() => {
            collections.strokes.set(input.strokeId, payload);
        }, this.localOrigin);
        this.undoManager.stopCapturing();
        return payload;
    }
    commitObject(input) {
        requireUuid(input.objectId, "objectId");
        const collections = this.#requirePage(input.pageId);
        if (collections.objects.has(input.objectId))
            throw new Error("whiteboard object ids are write-once");
        const text = input.kind === "text"
            ? normalizeWhiteboardText(input.text ?? "")
            : undefined;
        const payload = freezeObject({
            v: 1,
            kind: input.kind,
            x: requireFinite(input.x, "object x"),
            y: requireFinite(input.y, "object y"),
            ...(input.w === undefined
                ? {}
                : { w: requireFinite(input.w, "object width") }),
            ...(input.h === undefined
                ? {}
                : { h: requireFinite(input.h, "object height") }),
            ...(text === undefined ? {} : { text }),
            color: requireColor(input.color),
            ...(input.width === undefined
                ? {}
                : { width: requirePositive(input.width, "object line width") }),
            z: requireZ(input.z ??
                nextFractionalIndex([
                    ...collections.strokes.values(),
                    ...collections.objects.values(),
                ].map((value) => value.z))),
            createdBy: this.localOrigin.clientId,
            createdAt: requireFinite(input.createdAt, "createdAt"),
        });
        this.doc.transact(() => {
            collections.objects.set(input.objectId, payload);
        }, this.localOrigin);
        this.undoManager.stopCapturing();
        return payload;
    }
    deleteStroke(pageId, strokeId) {
        const strokes = this.#requirePage(pageId).strokes;
        if (!strokes.has(strokeId))
            return false;
        this.doc.transact(() => strokes.delete(strokeId), this.localOrigin);
        this.undoManager.stopCapturing();
        return true;
    }
    /**
     * Deletes every listed stroke in one transaction without closing the undo
     * capture. Drag-erase calls this per pointer sample between
     * beginEraseGesture()/endEraseGesture(), which collapse the whole drag
     * into a single undo step.
     */
    eraseStrokes(pageId, strokeIds) {
        const strokes = this.#requirePage(pageId).strokes;
        const present = strokeIds.filter((strokeId) => strokes.has(strokeId));
        if (present.length === 0)
            return 0;
        this.doc.transact(() => {
            for (const strokeId of present)
                strokes.delete(strokeId);
        }, this.localOrigin);
        return present.length;
    }
    /** Opens an undo capture that merges every eraseStrokes call in one drag. */
    beginEraseGesture() {
        this.undoManager.stopCapturing();
        this.undoManager.captureTimeout = Number.MAX_SAFE_INTEGER;
    }
    /** Closes the drag capture so the next edit starts its own undo step. */
    endEraseGesture() {
        this.undoManager.captureTimeout =
            PROTOCOL_CONSTANTS.WB_UNDO_CAPTURE_TIMEOUT;
        this.undoManager.stopCapturing();
    }
    deleteObject(pageId, objectId) {
        const objects = this.#requirePage(pageId).objects;
        if (!objects.has(objectId))
            return false;
        this.doc.transact(() => objects.delete(objectId), this.localOrigin);
        this.undoManager.stopCapturing();
        return true;
    }
    /** Removes every stroke and object on the page as one undoable step. */
    clearPage(pageId) {
        const collections = this.#requirePage(pageId);
        if (collections.strokes.size === 0 && collections.objects.size === 0)
            return false;
        this.doc.transact(() => {
            collections.strokes.clear();
            collections.objects.clear();
        }, this.localOrigin);
        this.undoManager.stopCapturing();
        return true;
    }
    undo() {
        if (!this.canUndo)
            return false;
        this.undoManager.undo();
        return true;
    }
    redo() {
        if (!this.canRedo)
            return false;
        this.undoManager.redo();
        return true;
    }
    get canUndo() {
        return this.undoManager.undoStack.length > 0;
    }
    get canRedo() {
        return this.undoManager.redoStack.length > 0;
    }
    observePages(listener) {
        const observer = () => listener();
        this.pages.observe(observer);
        return () => this.pages.unobserve(observer);
    }
    observeDocument(listener) {
        const observer = () => listener();
        this.pages.observeDeep(observer);
        return () => this.pages.unobserveDeep(observer);
    }
    observePage(pageId, listener) {
        const collections = this.#page(pageId);
        if (collections === undefined)
            return () => undefined;
        const observe = (collection) => (event) => {
            listener({
                collection,
                changes: [...event.changes.keys].map(([id, change]) => ({
                    id,
                    action: change.action,
                })),
            });
        };
        const strokeObserver = observe("strokes");
        const objectObserver = observe("objects");
        collections.strokes.observe(strokeObserver);
        collections.objects.observe(objectObserver);
        return () => {
            collections.strokes.unobserve(strokeObserver);
            collections.objects.unobserve(objectObserver);
        };
    }
    destroy() {
        this.undoManager.destroy();
    }
    #createPage(pageId, meta) {
        const page = new Y.Map();
        const strokes = new Y.Map();
        const objects = new Y.Map();
        this.doc.transact(() => {
            page.set("meta", Object.freeze({ ...meta }));
            page.set("strokes", strokes);
            page.set("objects", objects);
            this.pages.set(pageId, page);
        }, this.localOrigin);
        this.undoManager.addToScope([strokes, objects]);
        this.undoManager.stopCapturing();
    }
    #trackPage(pageId) {
        const collections = this.#page(pageId);
        if (collections !== undefined)
            this.undoManager.addToScope([collections.strokes, collections.objects]);
    }
    #requirePage(pageId) {
        const page = this.#page(pageId);
        if (page === undefined)
            throw new Error("whiteboard page is unavailable");
        return page;
    }
    #page(pageId) {
        const page = this.pages.get(pageId);
        if (!(page instanceof Y.Map))
            return undefined;
        const meta = page.get("meta");
        const strokes = page.get("strokes");
        const objects = page.get("objects");
        if (!isPageMeta(meta) ||
            !(strokes instanceof Y.Map) ||
            !(objects instanceof Y.Map))
            return undefined;
        return {
            page,
            meta,
            strokes: strokes,
            objects: objects,
        };
    }
}
export function normalizeWhiteboardText(value) {
    const normalized = value
        .normalize("NFC")
        .replace(unsafeTextCharacters, "")
        .normalize("NFC");
    if (normalized.length === 0)
        throw new Error("whiteboard text is empty after normalization");
    if ([...normalized].length > PROTOCOL_CONSTANTS.WB_TEXT_MAX_CP)
        throw new Error("whiteboard text exceeds the Unicode code-point limit");
    return normalized;
}
export function isStrokePayload(value) {
    if (!isRecord(value) || value.v !== 1)
        return false;
    return ((value.tool === "pen" ||
        value.tool === "highlighter" ||
        value.tool === "eraser-stroke") &&
        isColor(value.color) &&
        isPositive(value.width) &&
        Array.isArray(value.pts) &&
        value.pts.length >= 4 &&
        value.pts.length <= PROTOCOL_CONSTANTS.WB_MAX_PTS_PER_STROKE * 2 &&
        value.pts.length % 2 === 0 &&
        value.pts.every(Number.isFinite) &&
        isZ(value.z) &&
        typeof value.createdBy === "string" &&
        isFiniteNumber(value.createdAt));
}
export function isObjectPayload(value) {
    if (!isRecord(value) || value.v !== 1)
        return false;
    if (value.kind !== "text" &&
        value.kind !== "rect" &&
        value.kind !== "ellipse" &&
        value.kind !== "line" &&
        value.kind !== "arrow")
        return false;
    return (isFiniteNumber(value.x) &&
        isFiniteNumber(value.y) &&
        (value.w === undefined || isFiniteNumber(value.w)) &&
        (value.h === undefined || isFiniteNumber(value.h)) &&
        (value.kind === "text"
            ? typeof value.text === "string" && isNormalizedWhiteboardText(value.text)
            : value.text === undefined) &&
        isColor(value.color) &&
        (value.width === undefined || isPositive(value.width)) &&
        isZ(value.z) &&
        typeof value.createdBy === "string" &&
        isFiniteNumber(value.createdAt));
}
function isPageMeta(value) {
    return (isRecord(value) &&
        typeof value.name === "string" &&
        isFiniteNumber(value.createdAt) &&
        (value.background === undefined ||
            value.background === "plain" ||
            value.background === "grid"));
}
function freezeStroke(payload) {
    return Object.freeze({ ...payload, pts: Object.freeze([...payload.pts]) });
}
function freezeObject(payload) {
    return Object.freeze({ ...payload });
}
function isNormalizedWhiteboardText(value) {
    try {
        return normalizeWhiteboardText(value) === value;
    }
    catch {
        return false;
    }
}
function requireColor(value) {
    if (!isColor(value))
        throw new Error("whiteboard colors must be #rrggbb");
    return value.toLowerCase();
}
function isColor(value) {
    return typeof value === "string" && /^#[0-9a-f]{6}$/iu.test(value);
}
function requireFinite(value, name) {
    if (!Number.isFinite(value))
        throw new Error(`${name} must be finite`);
    return value;
}
function requirePositive(value, name) {
    if (!isPositive(value))
        throw new Error(`${name} must be positive`);
    return value;
}
function isPositive(value) {
    return typeof value === "number" && Number.isFinite(value) && value > 0;
}
function isFiniteNumber(value) {
    return typeof value === "number" && Number.isFinite(value);
}
function isRecord(value) {
    return typeof value === "object" && value !== null;
}
function requireUuid(value, name) {
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value))
        throw new Error(`${name} must be a UUID v4`);
}
function requireZ(value) {
    if (!isZ(value))
        throw new Error("whiteboard z-index must be fixed-width hexadecimal");
    return value;
}
function isZ(value) {
    return typeof value === "string" && /^[0-9a-f]{16}$/u.test(value);
}
function pageNumber(name) {
    return /^\d+$/u.test(name) ? Number(name) : Number.MAX_SAFE_INTEGER;
}
//# sourceMappingURL=model.js.map